import { toast } from 'react-toastify'
import { MobileModal } from 'components/UI/Modal/MobileModal'
import { useNavigate } from 'react-router-dom'
import FbButton from 'components/FbUI/FbButton'
import { useMemo } from 'react'
import { useController, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import apiService from 'services/api'
import to from 'await-to-js'
import { handleFormError } from 'utils/handleFormError'
import { TextInput } from 'components/FormUtils/TextInput'
import Select from 'components/FormUtils/Select'
import { PhoneInput } from 'components/FormUtils/PhoneInput'
import { TextareaInput } from 'components/FormUtils/TextareaInput'
import { z } from 'zod'
import {
  extensionZodValidator,
  phoneNumberZodValidor,
} from 'utils/zodValidators'

// Defines a TextFormInput component to display helper text and label above the input
import { FieldValues, Control, Path } from 'react-hook-form'
import { BaseTextInput } from 'components/FormUtils/TextInput/text-input'

type Props<T extends FieldValues> = {
  control: Control<T>
  name: Path<T>
}

export function AddressTextInput<T extends FieldValues>({
  control,
  name,
}: Props<T>) {
  // hook-form controller
  const {
    field: { onChange, value },
    fieldState: { error },
  } = useController({ name, control })

  return (
    <div className="flex flex-col w-full mt-[10px]">
      {/* headline */}
      <label
        htmlFor={name}
        className="text-base font-medium text-gray-800 select-none"
      >
        Address<span className="italic text-sm font-normal"> - optional</span>
      </label>

      {/* helper between label and input */}
      <span className="text-gray-600 text-xs italic leading-5 mb-1">
        If a company has multiple doors, add headquarters or leave empty
      </span>

      {/* reuse BaseTextInput only for the input itself */}
      <BaseTextInput
        name={name}
        value={value}
        onChange={onChange}
        error={error?.message}
        placeholder="Address of company"
        noMargin
      />
    </div>
  )
}

// Domain regex for validation
const domainRegex =
  /^(((?!-))(xn--|_)?[a-z0-9-]{0,61}[a-z0-9]{1,1}\.)*(xn--)?([a-z0-9][a-z0-9]{0,60}|[a-z0-9-]{1,30}\.[a-z]{2,})$/

// Zod schema for mobile company form
const mobileCompanyFormSchema = z.object({
  name: z.string().min(1, 'Company name is required'),
  domain: z
    .string()
    .trim()
    .optional()
    .nullable()
    .refine(
      (v) => {
        if (!v) return true
        return domainRegex.test(v) && v.includes('.')
      },
      { message: 'Enter domain as: domain.com' }
    ),
  category: z.number({
    required_error: 'Please select a category',
    invalid_type_error: 'Please select a category',
  }),
  sub_category: z.number({
    required_error: 'Please select a type',
    invalid_type_error: 'Please select a type',
  }),
  address: z.string().optional().nullable(),
  phone_number: phoneNumberZodValidor,
  extension: extensionZodValidator,
  distributors: z.array(z.number()).optional(),
  taglist: z.array(z.number()).optional(),
  notes: z.string().optional().nullable(),
})

type MobileCompanyFormValues = z.infer<typeof mobileCompanyFormSchema>

export default function MobileCompanyCreate() {
  const navigate = useNavigate()
  const api = apiService()

  const { data: categories } = api.useGetCompanyCategories()
  const { data: tagsOptions } = api.useGetTagsOptions()
  const { data: distributorsOptions } = api.useGetCompanyDistributorsOptions()

  const categoryOptions = useMemo(
    () =>
      categories?.map((category) => ({
        label: category.name,
        value: category.id,
      })) || [],
    [categories]
  )

  const {
    control,
    handleSubmit,
    formState: { isSubmitting, isValid },
    setError,
    watch,
  } = useForm<MobileCompanyFormValues>({
    mode: 'onChange',
    resolver: zodResolver(mobileCompanyFormSchema),
    defaultValues: {
      name: '',
      domain: '',
      address: '',
      notes: '',
      distributors: [],
      taglist: [],
    },
  })

  const watchCategory = watch('category')

  const typeOptions = useMemo(() => {
    if (!watchCategory || !categories) return []
    const categoryObj = categories.find((c) => c.id === watchCategory)
    if (!categoryObj) return []
    return categoryObj.sub_categories
      .map((subCategory) => ({
        label: subCategory.name,
        value: subCategory.id,
      }))
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [watchCategory, categories])

  const handleClose = () => {
    navigate('/')
  }

  const handleSubmitSuccess = () => {
    toast.success('Company created successfully')
    handleClose()
  }

  const submitApiAction = async (values: MobileCompanyFormValues) => {
    const payload = {
      ...values,
      country: 'US',
      state: '',
      city: '',
      domain: values.domain?.toLowerCase() || '',
      name: values.name?.trim() || '',
      address: values.address || '',
      zipcode: '',
    }
    return api.createCompany(payload)
  }

  const onSubmit = handleSubmit(
    async (values) => {
      const [err] = await to(submitApiAction(values))
      if (err) {
        console.warn(err)
        handleFormError(err, setError)
        return
      }
      handleSubmitSuccess()
    },
    (err) => console.warn(err)
  )

  return (
    <MobileModal
      handleBack={() => navigate(-1)}
      header={
        <div className="p-2">
          <div className="text-xs uppercase tracking-wide text-gray-500 mb-1">
            Create a company
          </div>
          <h1 className="text-2xl font-semibold text-gray-900">
            Add Company Details
          </h1>
          <div className="text-xs text-gray-500 mt-2">
            Add a company to First Bite to track additional deals and
            opportunities.
          </div>
        </div>
      }
      footer={
        <div className="flex gap-4 p-2 justify-between">
          <div className="w-1/2">
            <FbButton
              className="w-full"
              type="button"
              size="mdlg"
              variant="secondary"
              onClick={handleClose}
            >
              Cancel
            </FbButton>
          </div>
          <div className="w-1/2">
            <FbButton
              className="w-full"
              type="button"
              size="mdlg"
              variant="primary"
              onClick={onSubmit}
              disabled={!isValid || isSubmitting}
            >
              {isSubmitting ? 'Saving...' : 'Save'}
            </FbButton>
          </div>
        </div>
      }
    >
      <div className="px-2 space-y-4">
        <TextInput
          control={control}
          name="name"
          label="Company"
          placeholder="Start typing company name"
        />

        <TextInput
          control={control}
          name="domain"
          label="Company Domain"
          placeholder="https://www.company.com"
          optional
        />

        <div className="pt-2">
          <div className="text-base font-medium text-gray-800 mb-1">
            Target Categorization
          </div>
          <div className="space-y-2">
            <Select
              control={control}
              name="category"
              placeholder="Category"
              options={categoryOptions}
              className="mt-0 mb-2.5"
            />
            <Select
              control={control}
              name="sub_category"
              placeholder="Type"
              options={typeOptions}
              disabled={!watchCategory}
              className="mt-0"
            />
          </div>
        </div>

        <AddressTextInput control={control} name="address" />

        <PhoneInput
          control={control}
          name="phone_number"
          extensionName="extension"
          label="Phone Number"
          optional
        />

        <Select
          control={control}
          name="distributors"
          label="Account Management"
          placeholder="Known Distributors"
          options={distributorsOptions}
          isMulti
          optional
        />

        <Select
          control={control}
          name="taglist"
          label=""
          placeholder="Tag(s)"
          options={tagsOptions}
          isMulti
          optional
        />

        <TextareaInput
          control={control}
          name="notes"
          label="Notes"
          placeholder="Write additional notes here"
          optional
        />
      </div>
    </MobileModal>
  )
}
